import { Button, Icons, LiveTicker, SectionHeader } from "@jod/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import type { ReactNode } from "react";
import {
  CUSTOMER_DEMO_VENUE_ID,
  customerBackendApi,
  tableFromDto,
} from "../../features/api/backend.ts";
import { useT } from "../../features/i18n/index.ts";
import { useSession } from "../../features/session/session-context.tsx";
import { getMode } from "../../features/tonight/mode.ts";
import { activityLines, mockTables, mockVenue } from "../../mocks/data.ts";

export const Route = createFileRoute("/_tabs/home")({
  component: HomeScreen,
});

function HomeScreen() {
  const t = useT();
  const navigate = useNavigate();
  const { mode, tableCode, order, pendingInbox } = useSession();
  const modeMeta = getMode(mode);
  const backendEnabled = customerBackendApi !== null;
  const venueQuery = useQuery({
    queryKey: ["customer", "venue", CUSTOMER_DEMO_VENUE_ID],
    queryFn: () =>
      customerBackendApi?.getVenue(CUSTOMER_DEMO_VENUE_ID) ??
      Promise.reject(new Error("Customer backend API unavailable")),
    enabled: backendEnabled,
  });
  const tableQuery = useQuery({
    queryKey: ["customer", "tables", CUSTOMER_DEMO_VENUE_ID],
    queryFn: () =>
      customerBackendApi?.listTables(CUSTOMER_DEMO_VENUE_ID) ??
      Promise.reject(new Error("Customer backend API unavailable")),
    enabled: backendEnabled,
  });
  const venue = venueQuery.data;
  const tables = backendEnabled ? (tableQuery.data?.map(tableFromDto) ?? []) : mockTables;
  const venueName = backendEnabled ? (venue?.name ?? t("home.loadingVenue")) : mockVenue.name;
  const openTables = tables.filter((t) => t.mode !== "private").length;

  return (
    <div className="jod-stagger space-y-4">
      {/* live venue ticket */}
      <section className="glass-hi relative overflow-hidden rounded-xl px-5 py-5">
        <div className="jod-sheen" aria-hidden="true" />
        <div className="relative flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="jod-kicker">{mockVenue.session}</div>
            <h1 className="h-display mt-1 truncate text-[1.5rem]">{venueName}</h1>
          </div>
          <span className="shrink-0 rounded-full border border-hairline bg-[oklch(100%_0_0_/_5%)] px-3 py-1.5 font-semibold text-[0.7rem] text-gold">
            {t("common.table", { code: tableCode })}
          </span>
        </div>

        <div className="relative my-4">
          <span className="jod-notch" style={{ left: -31 }} aria-hidden="true" />
          <span className="jod-notch" style={{ right: -31 }} aria-hidden="true" />
          <div className="border-[var(--hairline-strong)] border-t border-dashed" />
        </div>

        <div className="relative flex items-center justify-between gap-2">
          <LiveTicker items={activityLines} />
          <span className="shrink-0 text-cream-3 text-xs">{mockVenue.lastCall}</span>
        </div>
      </section>

      {/* signature action — Send a drink */}
      <button
        type="button"
        onClick={() => navigate({ to: "/around" })}
        className="relative w-full overflow-hidden rounded-xl border border-[oklch(86%_0.18_120_/_35%)] bg-[linear-gradient(150deg,oklch(86%_0.18_120_/_16%),oklch(86%_0.18_120_/_4%))] px-5 py-5 text-left"
      >
        <div className="flex items-center gap-4">
          <span className="grid size-12 shrink-0 place-items-center rounded-lg bg-[oklch(86%_0.18_120_/_18%)] text-lime">
            <Icons.IconCheers size={26} />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-display font-semibold text-[1.1rem] text-cream">
              {t("home.sendDrinkTitle")}
            </div>
            <div className="text-cream-3 text-xs">{t("home.sendDrinkSub")}</div>
          </div>
          <span className="text-lime">
            <Icons.IconChevDown size={20} style={{ transform: "rotate(-90deg)" }} />
          </span>
        </div>
      </button>

      {/* utility row */}
      <div className="grid grid-cols-3 gap-2.5">
        <UtilityTile
          icon={<Icons.IconMenuGlyph size={22} />}
          label={t("home.tileMenu")}
          sub={t("menu.title")}
          onClick={() => navigate({ to: "/menu" })}
        />
        <UtilityTile
          icon={<Icons.IconAround size={22} />}
          label={t("nav.around")}
          sub={t("around.openCount", { count: openTables })}
          onClick={() => navigate({ to: "/around" })}
        />
        <UtilityTile
          icon={<Icons.IconAround size={22} />}
          label={t("home.tileFloor")}
          sub={t("home.tileFloorSub")}
          onClick={() => navigate({ to: "/room" })}
        />
        <UtilityTile
          icon={<Icons.IconGame size={22} />}
          label={t("home.tileGames")}
          sub={t("games.soon")}
          onClick={() => navigate({ to: "/games" })}
        />
        <UtilityTile
          icon={<span className="text-[1.2rem] leading-none">📺</span>}
          label={t("home.tileScreen")}
          sub={t("home.tileScreenSub")}
          onClick={() => navigate({ to: "/screen" })}
        />
      </div>

      {/* status strip */}
      <SectionHeader kicker={t("home.nowKicker")} title={t("home.ourTable")} />
      <div className="space-y-2.5">
        <StatusRow
          icon={<Icons.IconClock size={18} />}
          label={t("home.orderRow")}
          value={order ? t("home.orderInProgress") : t("order.none")}
          tone={order ? "lime" : "muted"}
          onClick={
            order
              ? () => navigate({ to: "/orders/$orderId", params: { orderId: "live" } })
              : undefined
          }
        />
        <StatusRow
          icon={<Icons.IconBell size={18} />}
          label={t("inbox.title")}
          value={
            pendingInbox > 0
              ? t("home.requestsPending", { count: pendingInbox })
              : t("home.requestsNone")
          }
          tone={pendingInbox > 0 ? "gold" : "muted"}
          onClick={() => navigate({ to: "/gifts/incoming" })}
        />
        <StatusRow
          icon={<Icons.IconEye size={18} />}
          label={t("tonightMode.kicker")}
          value={modeMeta.label}
          tone="muted"
          onClick={() => navigate({ to: "/tonight-mode" })}
        />
      </div>

      <Button variant="ghost" className="w-full" onClick={() => navigate({ to: "/menu" })}>
        <Icons.IconRepeat size={16} /> {t("home.reorder")}
      </Button>
    </div>
  );
}

interface UtilityTileProps {
  icon: ReactNode;
  label: string;
  sub: string;
  onClick: () => void;
}

function UtilityTile({ icon, label, sub, onClick }: UtilityTileProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="glass flex flex-col items-start gap-1.5 rounded-lg px-3.5 py-3.5 text-left"
    >
      <span className="text-gold">{icon}</span>
      <span className="font-display font-semibold text-cream text-sm">{label}</span>
      <span className="text-cream-3 text-[0.68rem]">{sub}</span>
    </button>
  );
}

interface StatusRowProps {
  icon: ReactNode;
  label: string;
  value: string;
  tone: "gold" | "lime" | "muted";
  onClick?: () => void;
}

function StatusRow({ icon, label, value, tone, onClick }: StatusRowProps) {
  const valueColor = tone === "lime" ? "text-lime" : tone === "gold" ? "text-gold" : "text-cream-3";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!onClick}
      className="glass flex w-full items-center gap-3 rounded-lg px-4 py-3.5 text-left disabled:opacity-100"
    >
      <span className="text-cream-2">{icon}</span>
      <span className="flex-1 text-cream-2 text-sm">{label}</span>
      <span className={`font-medium text-[0.8rem] ${valueColor}`}>{value}</span>
    </button>
  );
}
